import { useEffect, useState } from "react";
import AdminLayout from "@/components/AdminLayout";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Trophy, Ticket, Users, Coins, Flag, TextT, PlayCircle, Trash } from "@phosphor-icons/react";

const PRIZE_TYPES = [
  { key: "credits",             label: "Credits",             step: 0.1, Icon: Coins, tint: "text-blue-600 dark:text-blue-400" },
  { key: "banner_impressions",  label: "Banner Impressions",  step: 1,   Icon: Flag,  tint: "text-fuchsia-600 dark:text-fuchsia-400" },
  { key: "text_ad_impressions", label: "Text Ad Impressions", step: 1,   Icon: TextT, tint: "text-cyan-600 dark:text-cyan-400" },
];

const PAGE_SIZE = 25;

export default function AdminDraws() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(true);
  const [entrantsNow, setEntrantsNow] = useState(0);
  const [ticketsInPool, setTicketsInPool] = useState(0);
  // Drawing form
  const [drawing, setDrawing] = useState(false);
  const [form, setForm] = useState({ prize_type: "credits", prize_amount: 50, name: "" });

  const load = async (off) => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/draws?limit=${PAGE_SIZE}&offset=${off}`);
      setItems(data.items || []);
      setTotal(data.total || 0);
      setOffset(off);
      setEntrantsNow(data.entrants_now || 0);
      setTicketsInPool(data.tickets_in_pool || 0);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setLoading(false); }
  };

  useEffect(() => { load(0); }, []);

  const runDraw = async () => {
    if (drawing) return;
    if (!form.prize_amount || Number(form.prize_amount) <= 0) return toast.error("Prize amount must be greater than 0");
    if (!window.confirm(`Run drawing now?\n\nPrize: ${form.prize_amount} ${form.prize_type}\nEntrants: ${entrantsNow} · Tickets in pool: ${ticketsInPool}\n\nWinner's tickets will be consumed and prize awarded immediately.`)) return;
    setDrawing(true);
    try {
      const { data } = await api.post("/admin/draws", {
        prize_type: form.prize_type,
        prize_amount: Number(form.prize_amount),
        name: form.name?.trim() || null,
      });
      toast.success(`Winner: @${data.winner_username} — +${Number(data.prize_amount).toLocaleString()} ${data.prize_type_label}`);
      setForm({ ...form, name: "" });
      load(0);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setDrawing(false); }
  };

  const deleteDraw = async (d) => {
    const label = d.name ? `"${d.name}"` : `@${d.winner_username}'s win`;
    if (!window.confirm(`Delete this drawing entry?\n\n${label} — +${Number(d.prize_amount).toLocaleString()} ${d.prize_type_label}\n\nThe awarded prize stays with the winner — only the history row is removed.`)) return;
    try {
      await api.delete(`/admin/draws/${d.id}`);
      toast.success("Drawing entry deleted");
      // Reload current page (clamped if we deleted the last row on the last page)
      const newTotal = total - 1;
      const maxOff = Math.max(0, Math.floor((newTotal - 1) / PAGE_SIZE) * PAGE_SIZE);
      load(Math.min(offset, maxOff));
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const page = Math.floor(offset / PAGE_SIZE) + 1;

  return (
    <AdminLayout>
      <div className="mb-6">
        <div className="text-xs font-bold uppercase tracking-widest text-slate-500 dark:text-slate-400">Rewards</div>
        <h1 className="text-3xl font-black tracking-tight text-slate-900 dark:text-white inline-flex items-center gap-2" style={{ fontFamily: "'Cabinet Grotesk', system-ui, sans-serif" }}>
          <Trophy size={30} weight="fill" className="text-amber-500" />
          Surf Drawn Winners
        </h1>
        <p className="text-sm text-slate-500 dark:text-slate-400 mt-1 max-w-2xl">
          Run a ticket-weighted lottery drawing from every user currently holding Surf Drawn Tickets. Winner's tickets are consumed and the prize is awarded to their account instantly.
        </p>
      </div>

      <div className="grid md:grid-cols-3 gap-3 mb-6">
        <div data-testid="stat-entrants" className="rounded-2xl bg-gradient-to-br from-amber-500 to-orange-600 text-white p-5 shadow-sm">
          <div className="flex items-center justify-between">
            <Users size={22} weight="fill" className="opacity-90" />
            <div className="text-xs font-black uppercase tracking-widest opacity-80">Entrants now</div>
          </div>
          <div className="mt-2 text-4xl font-black" style={{ fontFamily: "'Cabinet Grotesk', system-ui, sans-serif" }}>{entrantsNow.toLocaleString()}</div>
        </div>
        <div data-testid="stat-tickets-pool" className="rounded-2xl bg-gradient-to-br from-yellow-500 to-amber-600 text-white p-5 shadow-sm">
          <div className="flex items-center justify-between">
            <Ticket size={22} weight="fill" className="opacity-90" />
            <div className="text-xs font-black uppercase tracking-widest opacity-80">Tickets in pool</div>
          </div>
          <div className="mt-2 text-4xl font-black" style={{ fontFamily: "'Cabinet Grotesk', system-ui, sans-serif" }}>{ticketsInPool.toLocaleString()}</div>
        </div>
        <div data-testid="stat-total-draws" className="rounded-2xl bg-gradient-to-br from-emerald-500 to-green-700 text-white p-5 shadow-sm">
          <div className="flex items-center justify-between">
            <Trophy size={22} weight="fill" className="opacity-90" />
            <div className="text-xs font-black uppercase tracking-widest opacity-80">Draws so far</div>
          </div>
          <div className="mt-2 text-4xl font-black" style={{ fontFamily: "'Cabinet Grotesk', system-ui, sans-serif" }}>{total.toLocaleString()}</div>
        </div>
      </div>

      <div data-testid="draw-form" className="rounded-2xl border border-amber-300 dark:border-amber-500/40 bg-amber-50 dark:bg-amber-500/5 p-5 mb-6">
        <div className="text-base font-black text-slate-900 dark:text-white mb-4">Run a new drawing</div>
        <div className="grid md:grid-cols-3 gap-4 mb-4">
          <label className="block">
            <span className="text-[10px] font-black uppercase tracking-widest text-slate-500">Prize type</span>
            <select
              data-testid="draw-form-type"
              value={form.prize_type}
              onChange={(e) => setForm({ ...form, prize_type: e.target.value })}
              className="w-full mt-1 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 px-3 py-2 text-sm text-slate-900 dark:text-slate-100"
            >
              {PRIZE_TYPES.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
            </select>
          </label>
          <label className="block">
            <span className="text-[10px] font-black uppercase tracking-widest text-slate-500">Prize amount</span>
            <input
              data-testid="draw-form-amount"
              type="number"
              min="0"
              step={PRIZE_TYPES.find((t) => t.key === form.prize_type)?.step || 1}
              value={form.prize_amount}
              onChange={(e) => setForm({ ...form, prize_amount: e.target.value })}
              className="w-full mt-1 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 px-3 py-2 text-sm text-slate-900 dark:text-slate-100"
            />
          </label>
          <label className="block">
            <span className="text-[10px] font-black uppercase tracking-widest text-slate-500">Label (optional)</span>
            <input
              data-testid="draw-form-name"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="e.g. Friday Big Draw"
              className="w-full mt-1 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 px-3 py-2 text-sm text-slate-900 dark:text-slate-100"
            />
          </label>
        </div>
        <button
          data-testid="draw-run-btn"
          onClick={runDraw}
          disabled={drawing || entrantsNow === 0}
          className="inline-flex items-center gap-2 rounded-full bg-amber-500 hover:bg-amber-400 text-amber-950 font-black px-5 py-2.5 text-sm shadow-sm disabled:opacity-50 transition-colors duration-200"
        >
          <PlayCircle size={18} weight="fill" /> {drawing ? "Drawing…" : "Run drawing now"}
        </button>
        {entrantsNow === 0 && (
          <div className="mt-3 text-xs text-slate-500">No users hold Surf Drawn Tickets yet — create a Prize Rule that awards tickets so members can earn them.</div>
        )}
      </div>

      <div className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 overflow-hidden">
        <div className="p-5 border-b border-slate-200 dark:border-slate-800 flex items-center justify-between">
          <div className="text-base font-black text-slate-900 dark:text-white">Drawing history</div>
          <div className="text-xs font-black uppercase tracking-widest text-slate-500">{total} draw{total !== 1 ? "s" : ""}</div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 dark:bg-slate-800/50 text-xs uppercase tracking-widest text-slate-500">
              <tr>
                <th className="p-3 text-left">Date</th>
                <th className="p-3 text-left">Winner</th>
                <th className="p-3 text-left">Prize</th>
                <th className="p-3 text-right">Tickets</th>
                <th className="p-3 text-right">Entrants</th>
                <th className="p-3 text-left">Label</th>
                <th className="p-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
              {loading && <tr><td colSpan={7} className="p-6 text-center text-slate-500">Loading…</td></tr>}
              {!loading && items.length === 0 && <tr><td colSpan={7} className="p-8 text-center text-slate-500">No draws yet.</td></tr>}
              {items.map((d) => {
                const meta = PRIZE_TYPES.find((t) => t.key === d.prize_type);
                const Icon = meta?.Icon || Trophy;
                return (
                  <tr key={d.id} data-testid={`admin-draw-row-${d.id}`}>
                    <td className="p-3 text-xs text-slate-500 whitespace-nowrap">{new Date(d.drawn_at).toLocaleString()}</td>
                    <td className="p-3 font-black text-slate-900 dark:text-white">@{d.winner_username}</td>
                    <td className="p-3">
                      <div className={`inline-flex items-center gap-1 text-xs font-black uppercase tracking-widest ${meta?.tint || "text-slate-500"}`}>
                        <Icon size={12} weight="fill" /> +{Number(d.prize_amount).toLocaleString()} {d.prize_type_label}
                      </div>
                    </td>
                    <td className="p-3 text-right font-black text-slate-900 dark:text-white">{d.tickets_at_draw.toLocaleString()}</td>
                    <td className="p-3 text-right text-slate-600 dark:text-slate-300">{d.entrant_count.toLocaleString()}</td>
                    <td className="p-3 text-xs text-amber-700 dark:text-amber-300">{d.name || <span className="text-slate-400">—</span>}</td>
                    <td className="p-3 text-right">
                      <button
                        data-testid={`admin-draw-delete-${d.id}`}
                        onClick={() => deleteDraw(d)}
                        title="Delete this drawing entry"
                        className="inline-flex items-center gap-1 text-red-600 dark:text-red-400 text-xs font-bold hover:underline"
                      >
                        <Trash size={12} weight="bold" /> delete
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {total > PAGE_SIZE && (
          <div className="p-3 flex items-center justify-between border-t border-slate-200 dark:border-slate-800">
            <button
              data-testid="admin-draws-prev"
              disabled={offset === 0 || loading}
              onClick={() => load(Math.max(0, offset - PAGE_SIZE))}
              className="text-xs font-bold rounded-full px-3 py-1.5 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 disabled:opacity-50"
            >
              Prev
            </button>
            <div className="text-xs text-slate-500">Page {page} of {totalPages}</div>
            <button
              data-testid="admin-draws-next"
              disabled={offset + PAGE_SIZE >= total || loading}
              onClick={() => load(offset + PAGE_SIZE)}
              className="text-xs font-bold rounded-full px-3 py-1.5 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 disabled:opacity-50"
            >
              Next
            </button>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
